/**
 * Where a signed-in session may be handed to, and how the request asking for it
 * is read.
 *
 * Zerops Code cannot run sign-up or third-party sign-in on its own origin —
 * Turnstile's site key is bound to this hostname, and the GitHub OAuth callback
 * is a fixed URL on Zerops' own OAuth App. So the user authenticates here and
 * we mint them a **personal access token** for that client and redirect it
 * back.
 *
 * A personal token rather than this account's own session: it is minted for
 * one client, it is revocable on its own from Settings without disturbing the
 * browser session, and nothing durable belonging to this sign-in ever leaves.
 * It stays user-scoped, so the client still sees every organization.
 *
 * **The client never names a destination.** It names a mode; this file maps the
 * mode to a callback. Nothing here parses a caller-supplied URL, so none of the
 * ways that is normally got wrong — `https://mate.zerops.io@evil.example`,
 * `mate.zerops.io.evil.example`, backslashes, encoded separators — can arise.
 *
 * The single value a caller contributes is a dev server's **port**, because a
 * dev server has no fixed one. It arrives as digits, is range-checked, and is
 * interpolated into a hostname and path fixed here. What that costs is the
 * ordinary loopback-redirect exposure (RFC 8252): a process already on the
 * user's machine could receive the session — and such a process can read the
 * browser profile anyway.
 *
 * The counterpart is `packages/client-runtime/src/zerops/handover.ts` in the
 * Zerops Code repository. Both halves are pinned by tests.
 */

/** The only mode served today. An unknown mode is refused before anything renders. */
export const APP_MODE_ZEROPS_CODE = 'zerops-code';

/** Where the hosted client is published. */
const ZEROPS_CODE_ORIGIN = 'https://mate.zerops.io';

/** Fixed both sides. Never taken from the request. */
const CALLBACK_PATH = '/zerops/authorized';

export interface AuthorizeAppRequest {
  app: typeof APP_MODE_ZEROPS_CODE;
  /** The client's nonce. Echoed back so the client can believe the callback. */
  state: string;
  /** A dev server's loopback port, already range-checked. */
  port: number | undefined;
  intent: 'register' | undefined;
}

export interface HandoverPayload {
  /** A freshly minted personal access token, shown by the API exactly once. */
  token: string;
  clientId: string | null;
  zcpClaimed: boolean;
}

const PORT_PATTERN = /^\d{1,5}$/;

/**
 * Reads the query into a request, or null when there is nothing servable in it.
 * Null means: render no consent screen and read no credential.
 */
export const parseAuthorizeAppRequest = (
  params: Record<string, string | undefined>
): AuthorizeAppRequest | null => {

  if (params['app'] !== APP_MODE_ZEROPS_CODE) {
    return null;
  }

  const state = params['state']?.trim();
  if (!state) {
    return null;
  }

  const intentParam = params['intent']?.trim();
  if (intentParam !== undefined && intentParam !== '' && intentParam !== 'register') {
    return null;
  }

  const portParam = params['port']?.trim();
  let port: number | undefined;
  if (portParam !== undefined && portParam !== '') {
    // Refused rather than ignored: dropping a malformed port would send the
    // credential to the hosted origin while a dev server waits for it.
    if (!PORT_PATTERN.test(portParam)) {
      return null;
    }
    port = Number(portParam);
    if (port < 1 || port > 65535) {
      return null;
    }
  }

  return {
    app: APP_MODE_ZEROPS_CODE,
    state,
    port,
    intent: intentParam === 'register' ? 'register' : undefined
  };
};

const callbackUrl = (request: AuthorizeAppRequest): URL => new URL(
  CALLBACK_PATH,
  request.port === undefined ? ZEROPS_CODE_ORIGIN : `http://localhost:${request.port}`
);

/**
 * The redirect that carries the token. The credential rides the **fragment**,
 * which no browser sends to a server: it stays out of access logs and out of
 * `Referer`. The client strips it from history on arrival.
 */
export const buildHandoverCallback = (
  request: AuthorizeAppRequest,
  payload: HandoverPayload
): string => {
  const url = callbackUrl(request);
  const fragment = new URLSearchParams();
  fragment.set('token', payload.token);
  fragment.set('state', request.state);
  if (payload.clientId) {
    fragment.set('clientId', payload.clientId);
  }
  if (payload.zcpClaimed) {
    fragment.set('zcpClaimed', 'true');
  }
  url.hash = fragment.toString();
  return url.toString();
};

/** The same trip with no credential — a cancel, or a failure the client should show. */
export const buildHandoverErrorCallback = (
  request: AuthorizeAppRequest,
  code: string
): string => {
  const url = callbackUrl(request);
  url.hash = new URLSearchParams({ error: code, state: request.state }).toString();
  return url.toString();
};

/**
 * What the consent screen shows as the destination. The whole origin, never a
 * friendly name: a local port has to be legible against the address people
 * expect, because it is the one part of the request the caller influenced.
 */
export const describeHandoverDestination = (
  request: AuthorizeAppRequest
): { origin: string; isLocal: boolean; } => {
  const url = callbackUrl(request);
  return { origin: url.origin, isLocal: request.port !== undefined };
};

/**
 * The inverse of `parseAuthorizeAppRequest`: the request as query params again,
 * for resuming after a sign-in. The two must round-trip — if they did not, the
 * resume would land back here and be refused as malformed, a silent dead end at
 * the end of a successful sign-in.
 */
export const asQuery = (request: AuthorizeAppRequest): Record<string, string> => ({
  app: request.app,
  state: request.state,
  ...(request.port === undefined ? {} : { port: String(request.port) }),
  ...(request.intent === undefined ? {} : { intent: request.intent })
});
